import Cloud from '@/assets/cloud.svg?react';
import cn from '@/utils/cn';

import { Trans } from '@lingui/react';
import UploadDropZone from '@rpldy/upload-drop-zone';
import { cva, type VariantProps } from 'class-variance-authority';

const dropzoneVariants = cva(
  'relative flex w-full flex-col items-center gap-2 rounded-[10px] bg-blue-100 px-4 py-6 text-center outline outline-1 -outline-offset-1 outline-white/0 transition-all duration-200 has-[:focus-visible]:outline-blue-600',
  {
    variants: {
      variant: {
        default: 'custom-dash group-hover:outline-blue-600',
        error: 'custom-dash-error group-hover:outline-red-400',
        disabled: 'custom-dash-disabled pointer-events-none',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface UploaderDropzoneProps
  extends VariantProps<typeof dropzoneVariants> {
  fileButton: React.ReactNode;
  className?: string;
}

export const UploaderDropzone = ({
  variant,
  fileButton,
  className,
}: UploaderDropzoneProps) => {
  const disabled = variant === 'disabled';

  const content = (
    <div
      className={dropzoneVariants({ variant })}
      data-testid="uploader-dropzone"
      aria-disabled={disabled}
    >
      <Cloud
        width={36}
        height={36}
        className={disabled ? 'text-dark-gray-300' : 'text-blue-600'}
      />
      <p
        className={cn(
          'body-large font-semibold',
          disabled ? 'text-dark-gray-300' : 'text-blue-600'
        )}
      >
        <Trans id="Wybierz plik lub przeciągnij go tutaj">
          Wybierz plik lub przeciągnij go tutaj
        </Trans>
      </p>
      <p
        className={cn(
          'body-small',
          disabled ? 'text-dark-gray-300' : 'text-dark-gray-400'
        )}
      >
        <Trans id="Akceptowane pliki: .jpg, .pdf, .jfif, .png, .heic, .zip, oraz .7zip.">
          Akceptowane pliki: .jpg, .pdf, .jfif, .png, .heic, .zip, oraz .7zip.
        </Trans>
      </p>
      {!disabled && fileButton}
    </div>
  );

  // disabled dropzone must not accept dropped files
  if (disabled) return <div className={cn('w-full', className)}>{content}</div>;

  return (
    <UploadDropZone
      className={cn('group w-full cursor-pointer', className)}
      onDragOverClassName="[&>div]:outline-blue-600"
    >
      {content}
    </UploadDropZone>
  );
};
